import { SEO_API_URL } from "./config";

const APP_NAME = "unsub";
const REQUEST_TIMEOUT_MS = 4_000;
const CACHE_PREFIX = "email-unsubscriber-seo-v2";

export type PostSummary = {
  slug: string;
  title: string;
  excerpt: string | null;
  publishedAt: string;
  readTime: number | null;
  tags: string[];
};

export type Post = PostSummary & {
  targetKeyword: string;
  content: string;
  metaTitle: string;
  metaDescription: string;
  seoKeywords: string[];
  wordCount: number | null;
  updatedAt: string;
};

export type ArticleResult<T> = {
  data: T;
  cached: boolean;
};

export class ArticleApiError extends Error {
  constructor(message = "The guide service did not respond.") {
    super(message);
    this.name = "ArticleApiError";
  }
}

function cacheKey(key: string) {
  return `${CACHE_PREFIX}:${key}`;
}

function readCache<T>(key: string): T | null {
  try {
    const cached = window.localStorage.getItem(cacheKey(key));
    return cached ? JSON.parse(cached) as T : null;
  } catch {
    return null;
  }
}

function writeCache<T>(key: string, value: T) {
  try {
    window.localStorage.setItem(cacheKey(key), JSON.stringify(value));
  } catch {
    // Storage may be disabled. Network delivery still works without it.
  }
}

async function request<T>(path: string, key: string, signal?: AbortSignal): Promise<ArticleResult<T>> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });

  try {
    const response = await fetch(`${SEO_API_URL}${path}`, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!response.ok) throw new ArticleApiError(`The guide service returned ${response.status}.`);
    const data = await response.json() as T;
    writeCache(key, data);
    return { data, cached: false };
  } catch (error) {
    if (signal?.aborted) throw error;
    const cached = readCache<T>(key);
    if (cached) return { data: cached, cached: true };
    throw new ArticleApiError(error instanceof Error && error.name === "AbortError"
      ? "The guide service took too long to respond."
      : "The guide service is temporarily unavailable.");
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}

export function getArticles(signal?: AbortSignal) {
  return request<PostSummary[]>(`/seo-v2/articles/${APP_NAME}?limit=100`, "articles", signal);
}

export function getArticle(slug: string, signal?: AbortSignal) {
  return request<Post>(`/seo-v2/articles/${APP_NAME}/${encodeURIComponent(slug)}`, `article:${slug}`, signal);
}

export function getCachedArticles() {
  return readCache<PostSummary[]>("articles");
}

export function getCachedArticle(slug: string) {
  return readCache<Post>(`article:${slug}`);
}
